import { ChevronDown, ChevronUp, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface ColumnEntry {
  id: string;
  label: string;
  visible: boolean;
  canHide: boolean;
}

export interface DataTableColumnsMenuProps {
  columns: readonly ColumnEntry[];
  onToggle: (id: string, visible: boolean) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onReset: () => void;
}

/**
 * Column visibility and order in one popover.
 *
 * Reordering is buttons rather than drag: it is reachable by keyboard without a
 * second interaction model, and moving a column is a rare, deliberate act.
 */
export function DataTableColumnsMenu({
  columns,
  onToggle,
  onMove,
  onReset,
}: DataTableColumnsMenuProps) {
  const hiddenCount = columns.filter((column) => !column.visible).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Settings2 className="size-3.5 text-muted-foreground" aria-hidden />
          Columnas
          {hiddenCount > 0 && (
            <span className="tabular text-xs text-muted-foreground">
              ({hiddenCount} {hiddenCount === 1 ? 'oculta' : 'ocultas'})
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-64 p-1">
        <p className="px-2 py-1.5 text-xs text-muted-foreground">Mostrar y ordenar columnas</p>

        <ul className="max-h-72 overflow-y-auto scrollbar-thin">
          {columns.map((column, index) => (
            <li key={column.id} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted">
              <Checkbox
                id={`col-${column.id}`}
                checked={column.visible}
                disabled={!column.canHide}
                onCheckedChange={(checked: boolean | 'indeterminate') =>
                  onToggle(column.id, checked === true)
                }
              />
              <label
                htmlFor={`col-${column.id}`}
                className={cn(
                  'min-w-0 flex-1 cursor-pointer truncate text-sm',
                  !column.visible && 'text-muted-foreground',
                )}
              >
                {column.label}
              </label>
              <span className="flex shrink-0">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Mover ${column.label} antes`}
                  disabled={index === 0}
                  onClick={() => onMove(column.id, -1)}
                >
                  <ChevronUp className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Mover ${column.label} después`}
                  disabled={index === columns.length - 1}
                  onClick={() => onMove(column.id, 1)}
                >
                  <ChevronDown className="size-3.5" />
                </Button>
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-1 border-t border-border p-1">
          <Button variant="ghost" size="sm" className="w-full justify-center" onClick={onReset}>
            Restablecer
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
